import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Clock, Check, X, Loader2, ChevronLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOT_TIMES = [];
for (let h = 9; h < 16; h++) {
  SLOT_TIMES.push(`${String(h).padStart(2, "0")}:00`);
  SLOT_TIMES.push(`${String(h).padStart(2, "0")}:30`);
}
const to12h = (t) => {
  const [h, m] = t.split(":").map(Number);
  const p = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${p}`;
};
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const upcomingDates = () => {
  const out = [];
  const cur = new Date();
  cur.setDate(cur.getDate() + 1);
  while (out.length < 16) {
    const day = cur.getDay();
    if (day >= 1 && day <= 4) out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

export default function ManageBooking() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [booking, setBooking] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound
  const [mode, setMode] = useState("view"); // view | reschedule
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [unavailable, setUnavailable] = useState([]);
  const [busy, setBusy] = useState(false);

  const dates = useMemo(() => upcomingDates(), []);

  useEffect(() => {
    axios
      .get(`${API}/bookings/${id}/manage`, { params: { token } })
      .then((r) => {
        setBooking(r.data);
        setState("ok");
      })
      .catch(() => setState("notfound"));
  }, [id, token]);

  useEffect(() => {
    if (!date) return;
    axios
      .get(`${API}/availability`, { params: { date } })
      .then((r) => setUnavailable(r.data.unavailable || []))
      .catch(() => setUnavailable([]));
  }, [date]);

  const prettyDate = (dstr) => {
    if (!dstr) return "";
    const [y, m, d] = dstr.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/bookings/${id}/cancel`, { token });
      setBooking({ ...booking, status: "cancelled" });
      toast.success("Your call has been cancelled.");
      setMode("view");
    } catch {
      toast.error("Could not cancel. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const reschedule = async () => {
    if (!date || !time) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bookings/${id}/reschedule`, { token, date, time });
      setBooking({ ...booking, date, time, status: "confirmed", slot_label: data.slot_label });
      toast.success("Your call has been rescheduled.");
      setMode("view");
      setDate(null);
      setTime(null);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not reschedule.");
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen grid place-items-center" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <Loader2 className="animate-spin" color="var(--rose-mocha)" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div
        className="min-h-screen grid place-items-center px-6 text-center font-body"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div>
          <h1 className="font-serif text-3xl mb-3" style={{ color: "var(--text-primary)" }}>
            Booking not found
          </h1>
          <p style={{ color: "var(--text-secondary)" }}>
            This link may be invalid or expired. Please <a href="/" className="link-underline">visit the site</a> to book again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen grid place-items-center px-4 py-10 font-body"
      style={{ backgroundColor: "var(--bg-secondary)" }}
    >
      <div
        data-testid="manage-booking-card"
        className="w-full max-w-lg rounded-3xl overflow-hidden shadow-xl"
        style={{ backgroundColor: "var(--bg-primary)", border: "1px solid var(--border-light)" }}
      >
        <div className="px-7 pt-7 pb-5" style={{ backgroundColor: "var(--bg-secondary)" }}>
          <p className="text-xs tracking-[0.28em] uppercase mb-2" style={{ color: "var(--rose-mocha-deep)" }}>
            Your discovery call
          </p>
          <h1 className="font-serif text-2xl md:text-3xl" style={{ color: "var(--text-primary)" }}>
            ProExec Virtual Solutions
          </h1>
        </div>

        <div className="px-7 py-6">
          {booking.status === "cancelled" ? (
            <div data-testid="manage-cancelled" className="text-center py-4">
              <span className="mx-auto grid place-items-center w-14 h-14 rounded-full mb-4" style={{ backgroundColor: "#F3DAD5" }}>
                <X size={26} color="var(--rose-mocha-deep)" />
              </span>
              <p className="text-lg mb-1" style={{ color: "var(--text-primary)" }}>This call was cancelled.</p>
              <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
                You're welcome to book again anytime.
              </p>
              <a
                href="/"
                className="inline-flex rounded-full px-7 py-3 text-sm font-medium"
                style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
              >
                Book a new call
              </a>
            </div>
          ) : mode === "view" ? (
            <div data-testid="manage-view">
              <p
                className="text-lg font-medium mb-2 rounded-xl px-4 py-3"
                style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)" }}
              >
                {booking.slot_label}
              </p>
              <p className="text-sm mb-8" style={{ color: "var(--text-secondary)" }}>
                Booked for {booking.name}. A 30-minute call.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  data-testid="manage-reschedule-button"
                  onClick={() => setMode("reschedule")}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium"
                  style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
                >
                  <Calendar size={15} /> Reschedule
                </button>
                <button
                  data-testid="manage-cancel-button"
                  onClick={cancel}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium disabled:opacity-60"
                  style={{ border: "1px solid var(--border-light)", color: "var(--rose-mocha-deep)" }}
                >
                  <X size={15} /> Cancel call
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="manage-reschedule">
              <button
                onClick={() => { setMode("view"); setDate(null); setTime(null); }}
                className="flex items-center gap-1 text-sm mb-4"
                style={{ color: "var(--rose-mocha-deep)" }}
              >
                <ChevronLeft size={16} /> Back
              </button>
              {!date ? (
                <>
                  <div className="flex items-center gap-2 mb-4" style={{ color: "var(--rose-mocha-deep)" }}>
                    <Calendar size={16} /> <span className="text-sm">Pick a new day</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2.5">
                    {dates.map((d) => (
                      <button
                        key={ymd(d)}
                        data-testid={`manage-date-${ymd(d)}`}
                        onClick={() => { setDate(ymd(d)); setTime(null); }}
                        className="rounded-xl px-2 py-3 text-center transition-transform hover:-translate-y-0.5"
                        style={{ border: "1px solid var(--border-light)", backgroundColor: "#fff" }}
                      >
                        <span className="block text-[11px] uppercase" style={{ color: "var(--rose-mocha-deep)" }}>
                          {d.toLocaleDateString("en-US", { weekday: "short" })}
                        </span>
                        <span className="block font-serif text-lg" style={{ color: "var(--text-primary)" }}>
                          {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <button onClick={() => setDate(null)} className="flex items-center gap-1 text-sm mb-3" style={{ color: "var(--rose-mocha-deep)" }}>
                    <ChevronLeft size={15} /> {prettyDate(date)}
                  </button>
                  <div className="flex items-center gap-2 mb-3" style={{ color: "var(--rose-mocha-deep)" }}>
                    <Clock size={16} /> <span className="text-sm">Pick a time (ET)</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2.5 mb-6">
                    {SLOT_TIMES.map((t) => {
                      const taken = unavailable.includes(t);
                      return (
                        <button
                          key={t}
                          data-testid={`manage-time-${t}`}
                          disabled={taken}
                          onClick={() => setTime(t)}
                          className="rounded-full px-2 py-2 text-sm transition-all disabled:opacity-35 disabled:line-through"
                          style={{
                            border: time === t ? "1px solid var(--rose-mocha)" : "1px solid var(--border-light)",
                            backgroundColor: time === t ? "var(--rose-mocha)" : "#fff",
                            color: time === t ? "#FBF6F3" : "var(--text-primary)",
                          }}
                        >
                          {to12h(t)}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    data-testid="manage-confirm-reschedule"
                    onClick={reschedule}
                    disabled={!time || busy}
                    className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-medium disabled:opacity-50"
                    style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
                  >
                    <Check size={15} /> {busy ? "Saving…" : "Confirm new time"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
